import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { FrameworkBadge } from '@/components/shared/FrameworkBadge';
import { Progress } from '@/components/ui/progress';
import { wordDiff, type WordToken } from '@/lib/wordDiff';
import type { DiffSection } from '@/types/api';
import { cn } from '@/lib/utils';

interface DiffSectionBlockProps {
  readonly section: DiffSection;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

const CHANGE_TYPE_BADGE: Record<
  DiffSection['changeType'],
  { label: string; className: string }
> = {
  added: { label: 'Added', className: 'bg-green-500/15 text-green-400' },
  modified: { label: 'Modified', className: 'bg-amber-500/15 text-amber-400' },
  removed: { label: 'Removed', className: 'bg-red-500/15 text-red-400' },
  unchanged: { label: 'Unchanged', className: 'bg-muted text-muted-foreground' },
};

function WordTokenSpan({
  tokens,
  side,
}: {
  readonly tokens: WordToken[];
  readonly side: 'left' | 'right';
}) {
  return (
    <span>
      {tokens.map((token, i) => {
        if (token.type === 'same') {
          return <span key={i}>{token.text} </span>;
        }
        if (token.type === 'removed' && side === 'left') {
          return (
            <span key={i} className="rounded-sm bg-red-500/20 text-red-300">
              {token.text}{' '}
            </span>
          );
        }
        if (token.type === 'added' && side === 'right') {
          return (
            <span key={i} className="rounded-sm bg-green-500/20 text-green-300">
              {token.text}{' '}
            </span>
          );
        }
        return <span key={i}>{token.text} </span>;
      })}
    </span>
  );
}

function DiffTextPanel({
  section,
  side,
}: {
  readonly section: DiffSection;
  readonly side: 'left' | 'right';
}) {
  const { changeType, currentText, proposedText } = section;

  if (side === 'left') {
    switch (changeType) {
      case 'added':
        return (
          <p className="text-sm italic text-muted-foreground">
            No current version
          </p>
        );
      case 'removed':
        return (
          <div className="rounded-md bg-compliance-critical/5 p-3">
            <p className="whitespace-pre-wrap text-sm leading-relaxed line-through text-muted-foreground">
              {currentText}
            </p>
          </div>
        );
      case 'unchanged':
        return (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">
            {currentText}
          </p>
        );
      case 'modified': {
        const diff = wordDiff(currentText, proposedText);
        return (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">
            <WordTokenSpan tokens={diff.left} side="left" />
          </p>
        );
      }
    }
  }

  // Right panel
  switch (changeType) {
    case 'added':
      return (
        <div className="rounded-md bg-green-500/5 p-3">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
            {proposedText}
          </p>
        </div>
      );
    case 'removed':
      return (
        <p className="text-sm italic text-muted-foreground">
          Section removed in proposed version
        </p>
      );
    case 'unchanged':
      return (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">
          {proposedText}
        </p>
      );
    case 'modified': {
      const diff = wordDiff(currentText, proposedText);
      return (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
          <WordTokenSpan tokens={diff.right} side="right" />
        </p>
      );
    }
  }
}

export function DiffSectionBlock({
  section,
  isSelected,
  onSelect,
}: DiffSectionBlockProps) {
  const [citationsOpen, setCitationsOpen] = useState(false);
  const badge = CHANGE_TYPE_BADGE[section.changeType];

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'rounded-lg border p-4 transition-colors',
        isSelected
          ? 'border-teal/30 bg-teal/5'
          : 'border-border hover:border-border/80',
      )}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Section header */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-foreground">
          {section.sectionTitle}
        </h3>
        <span
          className={cn(
            'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
            badge.className,
          )}
        >
          {badge.label}
        </span>
        <span className="text-xs text-muted-foreground">
          AI confidence: {Math.round(section.confidence * 100)}%
        </span>
      </div>

      {/* Two-panel diff */}
      <div className="grid grid-cols-2 gap-4">
        <div className="min-w-0 overflow-hidden break-words">
          <DiffTextPanel section={section} side="left" />
        </div>
        <div className="min-w-0 overflow-hidden break-words">
          <DiffTextPanel section={section} side="right" />
        </div>
      </div>

      {/* Citations */}
      {section.citations.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs font-medium text-teal hover:text-teal-light"
            onClick={(e) => {
              e.stopPropagation();
              setCitationsOpen(!citationsOpen);
            }}
          >
            {citationsOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {section.citations.length} regulatory citation
            {section.citations.length !== 1 ? 's' : ''}
          </button>

          {citationsOpen && (
            <div className="mt-3 space-y-3">
              {section.citations.map((citation) => (
                <div
                  key={citation.citationId}
                  className="rounded-md border border-border bg-muted/30 p-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <FrameworkBadge framework={citation.framework} />
                        <code className="font-mono text-xs text-muted-foreground">
                          {citation.ruleReference}
                        </code>
                      </div>
                      <p className="text-sm text-foreground">
                        {citation.ruleTitle}
                      </p>
                    </div>
                    <a
                      href={citation.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-teal hover:text-teal-light"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  <div className="mt-2">
                    <Progress value={citation.relevanceScore * 100} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
